import IconButton from '@components/v2/ui/IconButton/IconButton';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Typography from '@components/v2/ui/Typography/Typography';
import { SxProps, Theme } from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { MouseEvent } from 'react';
import { useMenu } from '../../../../../../../hooks/useMenu';
import LangEnum from '../../../../../../../lang/LangEnum';
import { OrderProductsTestIds } from '../../../../../../../utils/testIds/OrderProducts.testIds';
import { useTranslation } from '../../../../../../translation/TranslationContext';
import {
  OrderProductGroupBulkToggleConfig,
  OrderProductGroupToggleSize,
} from '../orderProductGroupHeader.types';

const GROUP_TOGGLE_SX: Record<OrderProductGroupToggleSize, SxProps<Theme>> = {
  small: { flexShrink: 0, width: 24, height: 24, color: 'action.active' },
  medium: { flexShrink: 0, width: 32, height: 32, color: 'action.active' },
};

interface OrderProductGroupToggleProps {
  isExpanded: boolean;
  size: OrderProductGroupToggleSize;
  bulkToggle?: OrderProductGroupBulkToggleConfig;
  onToggle: () => void;
}

const OrderProductGroupToggle = ({
  isExpanded,
  size,
  bulkToggle,
  onToggle,
}: OrderProductGroupToggleProps) => {
  const { translate } = useTranslation();
  const { anchorEl, isOpen, open, close, runAction } = useMenu();

  const bulkActionLabel = bulkToggle?.isExpanded
    ? LangEnum.ORDER_PRODUCTS_HIDE_ALL_LABEL
    : LangEnum.ORDER_PRODUCTS_EXPAND_ALL_LABEL;

  const openBulkMenu = (event: MouseEvent<HTMLButtonElement>) => {
    if (!bulkToggle) {
      return;
    }

    event.preventDefault();
    open(event);
  };

  const runBulkAction = (event: MouseEvent) => {
    if (!bulkToggle) {
      return;
    }

    runAction(event, bulkToggle.isExpanded ? bulkToggle.onCollapseAll : bulkToggle.onExpandAll);
  };

  return (
    <>
      <IconButton
        size={size}
        color="primary"
        shape="round"
        sx={GROUP_TOGGLE_SX[size]}
        icon={isExpanded ? ChevronDown : ChevronRight}
        ariaLabel={translate(LangEnum.ORDER_PRODUCTS_TOGGLE_GROUP_LABEL)}
        ariaExpanded={isExpanded}
        testId={OrderProductsTestIds.GROUP_HEADER_TOGGLE}
        onClick={onToggle}
        onContextMenu={openBulkMenu}
      />

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={close}
      >
        <MenuItem testId={OrderProductsTestIds.GROUP_HEADER_BULK_TOGGLE} onClick={runBulkAction}>
          <Typography variant="body2">{translate(bulkActionLabel)}</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default OrderProductGroupToggle;
